<template>
    <div
        class="detail-genre"
        @focusin="onFocusIn"
        @focusout="onFocusout"
    >
        <title-detail-genre class="detail-genre__title" :class="{'detail-genre__title_isExistSlider': !isEmptyMostPopular}"/>
        <MostPopular 
            ref="mostPopular"
            :slides="mostPopularSlides"
            @slide-down="focusFirstRow"
            @on-item-selected="itemsSelected"
            @ready="readyMostPopular"
        />
        <Lines-vod-detail-genre 
            ref="linesVodDetailGenre" 
            @ready="readyLines"
            @show-all="showAllItems"
            @list-item-selected="itemsSelected"
        />
    </div>
</template>

<script lang="ts" setup>
    import { ref, type Ref, computed, type ComputedRef, watch, onMounted, onUnmounted, nextTick } from "vue";
    import type { OnDemandCardData, CardData } from "@/components/Cards/types";
    import { PlayItem } from "@ca.vmedia.tv/domains";
    import { linkItems } from "@/components/Header/types/LinkItemsCollection";
    import { useApplicationStore } from "@/stores/Application";
    import type { LinesVodDetailGenreRef } from "@/components/widgets/LinesVodDetailGenre/types";
    import type { MostPopularRef } from "@/components/widgets/MostPopular/types";
    import { useRouter } from 'vue-router'
    import MostPopular from "@/components/widgets/MostPopular/MostPopular.vue";
    import { Logger } from '@/utils';
    import { useMostPopularVod } from "@/stores/MostPopularVod";
    import { storeToRefs } from 'pinia';
    import LinesVodDetailGenre from "@/components/widgets/LinesVodDetailGenre/LinesVodDetailGenre.vue";
    import { useParentControlStore } from "@/stores/ParentControl";
    import { useFocus } from "@/composables/useFocus";
    import { useDetailPageStore } from "@/stores/DetailPage";
    import { AppConstants } from "@/common";
    import { useVodPageStore } from "@/stores/VodPage";
    import { Strings } from '@/locales/types/enums';

    const VodPageStore = useVodPageStore();
    const {
        setCurrentGenre,
    } = VodPageStore;
    const {
        getCurrentGenre: genre,
    } = storeToRefs(VodPageStore);

    const { 
        setViewedDetailItems,
    } = useDetailPageStore();

    const ApplicationStore = useApplicationStore();
    const {
        getIsHaveFocusedElement: isHavSelectedIdProgram,
    } = storeToRefs(ApplicationStore);
    const {
        setTooltipeShowing,
        setTooltipeMessage
    } = ApplicationStore;

    const { setIsHaveFocusedElement, focusElementLine } = useFocus();

    const ParentControlStoreModule = useParentControlStore();
    const {
        getIsModalParentVisible: isTypeModeEnabledPin,
    } = storeToRefs(ParentControlStoreModule);
    
    const MostPopularVodPageStore = useMostPopularVod();

    const {
        getMostPopularVodDetailGenre: mostPopularSlides
    } = storeToRefs(MostPopularVodPageStore);

    definePageMeta({ 
        layout: 'default-no-header',
        middleware: ['set-page-indicator', 'fetch-vod-detail-genre'],
    });

    const { shelfKeys }: typeof AppConstants = AppConstants;
    const router = useRouter();
    const { $fire, $navigator, $eventBus } = useNuxtApp();
    const { t } = useI18n();

    const traceOnDemandScreen = $fire.trace($fire.performance, 'ON_DEMAND_SCREEN_DETAIL_GENRE');
    try {
        traceOnDemandScreen.start();
        Logger.captureLog('ON_DEMAND_SCREEN_DETAIL_GENRE start');
    } catch (e){
        Logger.captureLog(e);
    }

    const isReadyLines: Ref<boolean> = ref(false);
    const isReadyMostPopular: Ref<boolean> = ref(false);
    const isEmptyMostPopular: Ref<boolean> = ref(false);
    const linesVodDetailGenre: Ref<LinesVodDetailGenreRef | null> = ref(null);
    const mostPopular: Ref<MostPopularRef | null> = ref(null);

    const isReadyAll: ComputedRef<boolean> = computed((): boolean => isReadyLines.value && isReadyMostPopular.value && linesVodDetailGenre.value !== null && mostPopular.value !== null);

    const onFocusIn = (): void => {
        registerKeyListeners();
    };
    const onFocusout = (): void => {
        unregisterKeyListeners();
    };
    const registerKeyListeners = (): void => {
        $navigator.on("keypress:back", keyBackListener);
    };
    const unregisterKeyListeners = (): void => {
        $navigator.off("keypress:back", keyBackListener);
    };
    const keyBackListener = (): void => {
        router.go(-1);
    };
    const readyLines = (): void => {
        isReadyLines.value = true;
    };
    const readyMostPopular = (event: {isEmptySlider: boolean}): void => {
        if (event === undefined) {
            return;
        }
        isEmptyMostPopular.value = event.isEmptySlider;
        isReadyMostPopular.value = true;
    };
    const showAllItems = (val: string): void => {
        if (val === shelfKeys.genresCards) {
            showAllGenres();
        }
    };
    const showAllGenres = (): void => {
        if (genre.value) {
            setCurrentGenre(genre.value);
        }
        void router.push(linkItems.showAllForGenre.routerPath);
    };
    const focusMostPopular = (): void => {
        if (mostPopular.value) {
            mostPopular.value.focusMostPopular();
        }
    };
    const focusElement = (): void => {
        if (isHavSelectedIdProgram.value) {
            focusElementLine();
        } else {
            if (isEmptyMostPopular.value) {
                focusFirstRow();
            } else {
                focusMostPopular();
            }
        }
    };
    const focusFirstRow = (): void => {
        if (linesVodDetailGenre.value) {
            linesVodDetailGenre.value.focusFirstRow();
        }
    };
    const itemsSelected = (val: {lineId: string; item: CardData | OnDemandCardData}) => {
        if (PlayItem.isSeries((val.item as OnDemandCardData).getPlayItem())) {
            gotoSeriesDetailsPageVodDetailGenre(val.item as OnDemandCardData);
        } else {
            gotoMovieDetailsPageVodDetailGenre(val.item as OnDemandCardData);
        }
    };
    const gotoMovieDetailsPageVodDetailGenre = (item: OnDemandCardData): void => {
        try {
            const playItem = item.getPlayItem();
            if (!playItem) {
                throw new Error("playItem is null:" + JSON.stringify(item));
            }
            setViewedDetailItems(playItem);
            void router.push(`${linkItems.detailsVodMovies.routerPath}/${item.getId()}`);
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true});
        }
    };
    const gotoSeriesDetailsPageVodDetailGenre = (item: OnDemandCardData): void => {
        try {
            const playItem = item.getPlayItem();
            if (!playItem) {
                throw new Error("playItem is null:" + JSON.stringify(item));
            }
            setViewedDetailItems(playItem);
            void router.push(`${linkItems.detailsVodSeries.routerPath}/${item.getId()}`);
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true});
        }
    };

    const leaveLinesVodDetailGenre = (): void => {
        if(!isEmptyMostPopular.value) {
            focusMostPopular();
        }
    };

    onMounted(() => {
        void nextTick((): void => {
            $eventBus.on("key-up-rows", leaveLinesVodDetailGenre);
        });
    });

    onUnmounted(() => {
        $eventBus.off("key-up-rows", leaveLinesVodDetailGenre);
    });

    const handlerReady = (): void => {
        void nextTick(()=>{
            setIsHaveFocusedElement();
            if (!isTypeModeEnabledPin.value) {
                focusElement();
            }
        });

        if (traceOnDemandScreen) {
            try {
                traceOnDemandScreen.stop();
                Logger.captureLog(`ON_DEMAND_SCREEN_DETAIL_GENRE stop ${traceOnDemandScreen.durationUs / 1000000} sec`);
            } catch (e){
                Logger.captureLog(e);
            }
        }
    };

    watch(isReadyAll, (value: boolean): void => {
        if (value) {
            handlerReady();
        }
    })
</script>

<style lang="scss">
    @import url("styles.scss");
</style>